import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Card, Typography, Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Add } from 'iconsax-react';
import { useTaskStore } from '../../store/task.store';
import { useEmployeeStore } from '../../store/employee.store';
import { useTeamStore } from '../../store/team.store';
import { getTeamEmployees, getTeamTasks } from '../../services/team.service';
import { getSyncStatus, syncTasksOnce } from '../../services/task.service';
import { Task, TaskFilters } from '../../types/task.type';
import { getApiErrorDetail } from '../../utils/taskApi.utils';
import { useToast, TOAST_TYPES } from '../../providers/ToastProvider';
import {
  DueDateFilter,
  ObjectiveLinkFilter,
  TaskSortDir,
  TaskSortField,
  TaskStatusTab,
  WORK_STATUSES,
  buildToggleCompleteWrite,
  filterTasks,
  getSortLabel,
  getStatusCounts,
  getUniqueSpaces,
  getUniqueTaskLists,
  getLinkedObjectives,
  normalizeTask,
  sameTask,
  sortTasks,
  taskKey,
  toggleSortDir,
  workStatusLabelKey,
} from '../../utils/task.utils';
import TasksToolbar from '../../components/tasks/TasksToolbar';
import SortableColumnHeader from '../../components/tasks/SortableColumnHeader';
import TaskTableSkeleton from '../../components/tasks/TaskTableSkeleton';
import TasksRefreshButton from '../../components/tasks/TasksRefreshButton';
import VirtualTaskRows from '../../components/tasks/VirtualTaskRows';
import TaskRow, { ORG_TASK_GRID } from '../../components/tasks/TaskRow';
import TaskFormDialog from '../../components/tasks/TaskFormDialog';
import DeleteTaskDialog from '../../components/tasks/DeleteTaskDialog';

const ORG_GRID = ORG_TASK_GRID;

const DUE_FILTER_LABELS: Record<DueDateFilter, string> = {
  '': '',
  overdue: 'tasks.dueFilters.overdue',
  thisWeek: 'tasks.dueFilters.thisWeek',
  noDue: 'tasks.dueFilters.noDue',
};

export default function TasksPage() {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const {
    tasks, isLoading, isRefreshing, fetchTasks, syncAndRefreshTasks,
    createEmployeeTask, updateEmployeeTask, deleteEmployeeTask,
  } = useTaskStore();
  const { employees, fetchEmployees }    = useEmployeeStore();
  const { teams, fetchTeams }            = useTeamStore();
  const [filters, setFilters]            = useState<TaskFilters & { dueDateFilter?: DueDateFilter; objectiveLink?: ObjectiveLinkFilter; objectiveId?: string }>({});
  const [activeTab, setActiveTab]        = useState<TaskStatusTab>('all');
  const [selectedTeam, setSelectedTeam]  = useState('');
  const [teamTasks, setTeamTasks]        = useState<Task[]>([]);
  const [teamTasksLoading, setTeamTasksLoading] = useState(false);
  const [teamTasksRefreshing, setTeamTasksRefreshing] = useState(false);
  const [teamEmpIds, setTeamEmpIds]      = useState<Set<string> | null>(null);
  const [teamLoading, setTeamLoading]    = useState(false);
  const [lastSyncedAt, setLastSyncedAt]  = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [sortField, setSortField]        = useState<TaskSortField>('due');
  const [sortDir, setSortDir]            = useState<TaskSortDir>('asc');
  const prevTeam = useRef('');
  const teamFromUrl = searchParams.get('team');
  const [formOpen, setFormOpen] = useState(false);
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null);
  const [mutatingId, setMutatingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchEmployees();
    fetchTeams();
    const fetchedAt = useTaskStore.getState().tasksFetchedAt;
    if (!fetchedAt || Date.now() - fetchedAt > 20000) fetchTasks();

    (async () => {
      try {
        const status = await getSyncStatus();
        if (cancelled) return;
        setLastSyncedAt(status.last_synced_at);
        if (status.last_synced_at != null) return;
        setSyncing(true);
        try {
          await syncTasksOnce();
          if (cancelled) return;
          const refreshed = await getSyncStatus();
          if (!cancelled) setLastSyncedAt(refreshed.last_synced_at);
          await fetchTasks();
        } catch (e: any) {
          if (!cancelled) {
            showToast(
              e?.response?.status === 403 ? t('tasks.googleRequiredTitle') : getApiErrorDetail(e, t('common.error')),
              TOAST_TYPES.ERROR,
            );
          }
        } finally {
          if (!cancelled) setSyncing(false);
        }
      } catch {
        // The list is already loading from the database.
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (teamFromUrl && teams.some(t => t.id === teamFromUrl)) {
      setSelectedTeam(teamFromUrl);
    }
  }, [teamFromUrl, teams]);

  useEffect(() => {
    if (!selectedTeam) {
      setTeamTasks([]);
      setTeamTasksLoading(false);
      return;
    }
    setTeamTasks([]);
    setTeamTasksLoading(true);
    getTeamTasks(selectedTeam)
      .then((res: any) => {
        const data = (res.data ?? []).map(normalizeTask);
        setTeamTasks(data);
      })
      .catch(() => setTeamTasks([]))
      .finally(() => setTeamTasksLoading(false));
  }, [selectedTeam]);

  const handleRefresh = async () => {
    try {
      if (selectedTeam) {
        setTeamTasksRefreshing(true);
        await syncTasksOnce();
        const res: any = await getTeamTasks(selectedTeam);
        setTeamTasks((res.data ?? []).map(normalizeTask));
      } else {
        await syncAndRefreshTasks({});
      }
      const status = await getSyncStatus();
      setLastSyncedAt(status.last_synced_at);
    } catch (e: any) {
      showToast(
        e?.response?.status === 403 ? t('tasks.googleRequiredTitle') : getApiErrorDetail(e, t('common.error')),
        TOAST_TYPES.ERROR,
      );
    } finally {
      setTeamTasksRefreshing(false);
    }
  };

  useEffect(() => {
    if (selectedTeam === prevTeam.current) return;
    prevTeam.current = selectedTeam;
    if (!selectedTeam) { setTeamEmpIds(null); return; }
    setTeamLoading(true);
    getTeamEmployees(selectedTeam)
      .then((res: any) => {
        const list: { id: string }[] = Array.isArray(res) ? res : (res.data ?? []);
        setTeamEmpIds(new Set(list.map(e => e.id)));
      })
      .catch(() => setTeamEmpIds(new Set()))
      .finally(() => setTeamLoading(false));
  }, [selectedTeam]);

  const taskSource = selectedTeam ? teamTasks : tasks;
  const pageLoading = selectedTeam ? teamTasksLoading : isLoading;
  const pageRefreshing = selectedTeam ? teamTasksRefreshing : isRefreshing;

  const syncTeamTask = (task: Task) => {
    if (!selectedTeam) return;
    setTeamTasks(prev => {
      const nextTask = normalizeTask(task);
      const idx = prev.findIndex(t => sameTask(t, nextTask));
      const next = idx >= 0 ? prev.map((t, i) => (i === idx ? nextTask : t)) : [nextTask, ...prev];
      return next;
    });
  };

  const removeTeamTask = (googleTaskId: string) => {
    if (!selectedTeam) return;
    setTeamTasks(prev => {
      const next = prev.filter(t => t.google_task_id !== googleTaskId);
      return next;
    });
  };

  const handleTaskCreated = (task: Task) => {
    if (selectedTeam) syncTeamTask(task);
    showToast(t('tasks.taskCreated'), TOAST_TYPES.SUCCESS);
  };

  const handleTaskUpdated = (task: Task) => {
    if (selectedTeam) syncTeamTask(task);
    showToast(t('tasks.taskUpdated'), TOAST_TYPES.SUCCESS);
  };

  const handleToggleComplete = async (task: Task) => {
    setMutatingId(task.google_task_id);
    try {
      const updated = await updateEmployeeTask(task.google_task_id, {
        employee_id: task.employee_id,
        task_list_id: task.task_list_id,
        ...buildToggleCompleteWrite(task),
      });
      if (selectedTeam) syncTeamTask(updated);
    } catch (e) {
      showToast(getApiErrorDetail(e, t('common.error')), TOAST_TYPES.ERROR);
    } finally {
      setMutatingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteEmployeeTask(deleteTarget);
      removeTeamTask(deleteTarget.google_task_id);
      showToast(t('tasks.taskDeleted'), TOAST_TYPES.SUCCESS);
      setDeleteTarget(null);
    } catch (e) {
      showToast(getApiErrorDetail(e, t('common.error')), TOAST_TYPES.ERROR);
    } finally {
      setDeleting(false);
    }
  };

  const connectedEmployees = employees.filter(e => e.google_access_status === 'approved');
  const spaces = useMemo(() => getUniqueSpaces(taskSource), [taskSource]);
  const taskLists = useMemo(() => getUniqueTaskLists(taskSource), [taskSource]);
  const linkedObjectives = useMemo(() => getLinkedObjectives(taskSource), [taskSource]);

  const filteredEmployeeOptions = useMemo(() => {
    const pool = selectedTeam && teamEmpIds
      ? connectedEmployees.filter(e => teamEmpIds.has(e.id))
      : connectedEmployees;
    return pool.map(e => ({ id: e.id, label: e.name }));
  }, [connectedEmployees, selectedTeam, teamEmpIds]);

  const filtered = useMemo(() => {
    const result = filterTasks(taskSource, {
      search: filters.search,
      employee_id: filters.employee_id,
      space_name: filters.space_name,
      task_list_id: filters.task_list_id,
      dueDateFilter: filters.dueDateFilter,
      objectiveLink: filters.objectiveLink,
      objectiveId: filters.objectiveLink === 'linked' ? filters.objectiveId : '',
      statusTab: activeTab,
      teamEmployeeIds: selectedTeam ? null : teamEmpIds,
    });
    return sortTasks(result, sortField, sortDir);
  }, [taskSource, filters, activeTab, teamEmpIds, selectedTeam, sortField, sortDir]);

  const counts = useMemo(() => getStatusCounts(taskSource), [taskSource]);

  const statusTabs = [
    { key: 'all' as const, label: t('tasks.statusAll'), count: counts.all },
    ...WORK_STATUSES.map(status => ({
      key: status,
      label: t(workStatusLabelKey(status)),
      count: counts[status],
    })),
  ];

  const handleSort = (field: TaskSortField) => {
    setSortDir(toggleSortDir(sortField, sortDir, field));
    setSortField(field);
  };

  const handleDueSortToggle = () => {
    setSortField('due');
    setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const activeChips = useMemo(() => {
    const chips = [];
    if (selectedTeam) {
      const team = teams.find(tm => tm.id === selectedTeam);
      chips.push({
        key: 'team',
        label: `${t('layout.teams')}: ${team?.name ?? ''}`,
        onRemove: () => { setSelectedTeam(''); setFilters(f => ({ ...f, employee_id: '' })); },
      });
    }
    if (filters.employee_id) {
      const emp = connectedEmployees.find(e => e.id === filters.employee_id);
      chips.push({
        key: 'employee',
        label: `${t('tasks.employee')}: ${emp?.name ?? ''}`,
        onRemove: () => setFilters(f => ({ ...f, employee_id: '' })),
      });
    }
    if (filters.space_name) {
      chips.push({
        key: 'space',
        label: `${t('tasks.space')}: ${filters.space_name}`,
        onRemove: () => setFilters(f => ({ ...f, space_name: '' })),
      });
    }
    if (filters.task_list_id) {
      const list = taskLists.find(l => l.id === filters.task_list_id);
      chips.push({
        key: 'list',
        label: `${t('tasks.googleList')}: ${list?.name ?? ''}`,
        onRemove: () => setFilters(f => ({ ...f, task_list_id: '' })),
      });
    }
    if (filters.dueDateFilter) {
      chips.push({
        key: 'due',
        label: t(DUE_FILTER_LABELS[filters.dueDateFilter]),
        onRemove: () => setFilters(f => ({ ...f, dueDateFilter: '' })),
      });
    }
    if (filters.objectiveLink) {
      chips.push({
        key: 'objective',
        label: filters.objectiveLink === 'linked' ? t('tasks.linked') : t('tasks.notLinked'),
        onRemove: () => setFilters(f => ({ ...f, objectiveLink: '', objectiveId: '' })),
      });
    }
    if (filters.objectiveLink === 'linked' && filters.objectiveId) {
      const objective = linkedObjectives.find(o => o.id === filters.objectiveId);
      chips.push({
        key: 'objectiveId',
        label: `${t('tasks.linkedObjective')}: ${objective?.title ?? ''}`,
        onRemove: () => setFilters(f => ({ ...f, objectiveId: '' })),
      });
    }
    return chips;
  }, [filters, selectedTeam, teams, connectedEmployees, taskLists, linkedObjectives, t]);

  const filterGroups = useMemo(() => [
    {
      key: 'team',
      label: t('layout.teams'),
      value: selectedTeam,
      allLabel: t('tasks.allTeams'),
      options: teams.map(team => ({ id: team.id, label: team.name })),
      onChange: (v: string) => { setSelectedTeam(v); setFilters(f => ({ ...f, employee_id: '' })); },
      loading: teamLoading,
      hidden: teams.length === 0,
    },
    {
      key: 'employee',
      label: t('tasks.employee'),
      value: filters.employee_id || '',
      allLabel: t('tasks.allEmployees'),
      options: filteredEmployeeOptions,
      onChange: (v: string) => setFilters(f => ({ ...f, employee_id: v })),
      hidden: connectedEmployees.length === 0,
    },
    {
      key: 'space',
      label: t('tasks.space'),
      value: filters.space_name || '',
      allLabel: t('tasks.allSpaces'),
      options: spaces.map(name => ({ id: name, label: name })),
      onChange: (v: string) => setFilters(f => ({ ...f, space_name: v })),
    },
    {
      key: 'list',
      label: t('tasks.googleList'),
      value: filters.task_list_id || '',
      allLabel: t('tasks.allTaskLists'),
      options: taskLists.map(l => ({ id: l.id, label: l.name })),
      onChange: (v: string) => setFilters(f => ({ ...f, task_list_id: v })),
      hidden: taskLists.length <= 1,
    },
    {
      key: 'due',
      label: t('tasks.dueDateFilter'),
      value: filters.dueDateFilter || '',
      allLabel: t('tasks.dueFilters.all'),
      options: [
        { id: 'overdue', label: t('tasks.dueFilters.overdue') },
        { id: 'thisWeek', label: t('tasks.dueFilters.thisWeek') },
        { id: 'noDue', label: t('tasks.dueFilters.noDue') },
      ],
      onChange: (v: string) => setFilters(f => ({ ...f, dueDateFilter: v as DueDateFilter })),
    },
    {
      key: 'objective',
      label: t('tasks.objectiveFilter'),
      value: filters.objectiveLink || '',
      allLabel: t('tasks.allTasks'),
      options: [
        { id: 'linked', label: t('tasks.linked') },
        { id: 'unlinked', label: t('tasks.notLinked') },
      ],
      onChange: (v: string) => setFilters(f => ({
        ...f,
        objectiveLink: v as ObjectiveLinkFilter,
        objectiveId: v === 'linked' ? f.objectiveId : '',
      })),
    },
    {
      key: 'objectiveId',
      label: t('tasks.linkedObjective'),
      value: filters.objectiveId || '',
      allLabel: t('tasks.allObjectives'),
      options: linkedObjectives.map(o => ({ id: o.id, label: o.title })),
      onChange: (v: string) => setFilters(f => ({ ...f, objectiveId: v })),
      hidden: filters.objectiveLink !== 'linked',
    },
  ], [filters, selectedTeam, teams, teamLoading, filteredEmployeeOptions, connectedEmployees, spaces, taskLists, linkedObjectives, t]);

  const employeeOptionsForForm = useMemo(
    () => filteredEmployeeOptions.map(e => ({ id: e.id, name: e.label })),
    [filteredEmployeeOptions],
  );

  const columns: { label: string; field: TaskSortField }[] = [
    { label: t('tasks.tableTask'), field: 'title' },
    { label: t('tasks.tableSpace'), field: 'space' },
    { label: t('tasks.tableAssignee'), field: 'employee' },
    { label: t('tasks.tableDueDate'), field: 'due' },
    { label: t('tasks.tableStatus'), field: 'status' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.3px' }}>
            {t('tasks.title')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 0.5 }}>
            {t('tasks.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add size={16} color="#fff" />}
            onClick={() => { setEditTask(null); setFormOpen(true); }}
            sx={{ textTransform: 'none' }}
          >
            {t('tasks.addTask')}
          </Button>
          <TasksRefreshButton onClick={handleRefresh} refreshing={pageRefreshing} />
          {!selectedTeam && lastSyncedAt && (
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
              {t('tasks.lastSynced')}: {new Date(lastSyncedAt).toLocaleString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Typography>
          )}
        </Box>
      </Box>

      <TasksToolbar
        searchValue={filters.search || ''}
        searchPlaceholder={t('tasks.searchPlaceholder')}
        onSearchChange={v => setFilters(f => ({ ...f, search: v }))}
        statusTabs={statusTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        filterGroups={filterGroups}
        activeChips={activeChips}
        onClearAll={() => { setFilters({}); setSelectedTeam(''); }}
        sortLabel={getSortLabel(sortField, sortDir, t)}
        onSortToggle={handleDueSortToggle}
        filteredCount={filtered.length}
        totalCount={taskSource.length}
      />

      {pageLoading ? (
        <TaskTableSkeleton variant="org" rows={10} />
      ) : (
        <Card sx={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: ORG_GRID, px: 2.5, py: 1.25, gap: 2, borderBottom: '1px solid #F3F4F6', bgcolor: '#F9FAFB' }}>
            {columns.map(col => (
              <SortableColumnHeader
                key={col.field}
                label={col.label}
                field={col.field}
                activeField={sortField}
                activeDir={sortDir}
                onSort={handleSort}
              />
            ))}
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textAlign: 'end' }}>
              {t('common.actions')}
            </Typography>
          </Box>
          {filtered.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                {syncing ? t('tasks.syncing') : t('tasks.noTasks')}
              </Typography>
            </Box>
          ) : (
            <VirtualTaskRows
              items={filtered}
              getKey={taskKey}
              renderItem={task => (
                <TaskRow
                  task={task}
                  actionsLoading={mutatingId === task.google_task_id}
                  onToggleComplete={handleToggleComplete}
                  onEdit={t => { setEditTask(t); setFormOpen(true); }}
                  onDelete={setDeleteTarget}
                />
              )}
            />
          )}
        </Card>
      )}

      <TaskFormDialog
        open={formOpen}
        mode="employee"
        task={editTask}
        employeeOptions={employeeOptionsForForm}
        defaultEmployeeId={filters.employee_id || filteredEmployeeOptions[0]?.id}
        onClose={() => { setFormOpen(false); setEditTask(null); }}
        onSubmitEmployee={async (payload) => {
          const task = await createEmployeeTask(payload);
          handleTaskCreated(task);
        }}
        onSubmitMy={async () => {}}
        onSubmitEditEmployee={async (payload) => {
          if (!editTask) return;
          const task = await updateEmployeeTask(editTask.google_task_id, payload);
          handleTaskUpdated(task);
        }}
        onSubmitEditMy={async () => {}}
      />

      <DeleteTaskDialog
        open={!!deleteTarget}
        task={deleteTarget}
        loading={deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
}
